import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList
} from 'recharts';
import './FunnelChart.css';

/**
 * FunnelChart Component
 * Displays funnel efficiency as horizontal bars with conversion rates
 */
function FunnelChart({ data, loading }) {
    if (loading) {
        return (
            <div className="chart-container chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="chart-container chart-empty">
                <p>No funnel data available</p>
            </div>
        );
    }

    // Generate gradient colors from light orange to dark orange
    const getBarColor = (index, total) => {
        const colors = [
            '#FDE8D3', // Lightest
            '#FADCBD',
            '#F7CFA6',
            '#F4C290',
            '#F1B47A',
            '#EDA664',
            '#E99750',
            '#E67E22'  // Darkest (primary orange)
        ];
        const colorIndex = Math.min(Math.floor((index / (total - 1)) * (colors.length - 1)), colors.length - 1);
        return colors[colorIndex] || colors[colors.length - 1];
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip-label">{data.stage}</p>
                    <p className="chart-tooltip-item">
                        <strong>{data.count}</strong> candidates
                    </p>
                    {data.conversionRate < 100 && (
                        <p className="chart-tooltip-item">
                            Conversion: <strong>{data.conversionRate}%</strong>
                        </p>
                    )}
                </div>
            );
        }
        return null;
    };

    // Prepare data with percentage of max for visualization
    const maxCount = Math.max(...data.map(d => d.count));
    const chartData = data.map((item, index) => ({
        ...item,
        percentage: (item.count / maxCount) * 100,
        displayLabel: `${item.count} (${item.conversionRate}%)`
    }));

    return (
        <div className="chart-container funnel-chart">
            <div className="chart-header">
                <h3 className="chart-title">Funnel Efficiency</h3>
                <span className="chart-subtitle">Candidates by stage with conversion rates</span>
            </div>

            <div className="funnel-content">
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 80, left: 120, bottom: 10 }}
                    >
                        <XAxis type="number" hide />
                        <YAxis
                            type="category"
                            dataKey="stage"
                            tick={{ fontSize: 13, fill: '#2C3E50' }}
                            axisLine={false}
                            tickLine={false}
                            width={110}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                            dataKey="count"
                            radius={[0, 4, 4, 0]}
                            barSize={28}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={getBarColor(index, chartData.length)}
                                />
                            ))}
                            <LabelList
                                dataKey="count"
                                position="right"
                                formatter={(value) => value.toLocaleString()}
                                style={{ fontSize: 12, fill: '#2C3E50', fontWeight: 500 }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>

                {/* Conversion rate indicators */}
                <div className="funnel-conversions">
                    {chartData.slice(1).map((item, index) => (
                        <div key={index} className="conversion-indicator">
                            <div className="conversion-arrow">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#95A5A6" strokeWidth="2">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </div>
                            <span className={`conversion-rate ${item.conversionRate < 50 ? 'low' : item.conversionRate < 75 ? 'medium' : 'high'}`}>
                                {item.conversionRate}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default FunnelChart;
